/**
 * Oath User Management Service
 * API calls for user and permission management
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// User Management Endpoints
// ============================================

/**
 * Get all users under an agency
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, users?: Array, error?: string}>}
 */
export const getAgencyUsers = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/users`);

    if (!response.ok) {
      // Treat 404 or 204 as "no users" rather than an error
      if (response.status === 404 || response.status === 204) {
        return { success: true, users: [] };
      }
      const errorData = await parseResponse(response);
      const errorMessage = errorData.message || '';
      // Also treat "no users" type messages as empty list
      if (errorMessage.toLowerCase().includes('no users') ||
          errorMessage.toLowerCase().includes('not found') ||
          errorMessage.toLowerCase().includes('empty')) {
        return { success: true, users: [] };
      }
      return { success: false, error: errorMessage || 'Failed to fetch users' };
    }

    const users = await parseResponse(response);
    // Ensure we always return an array - handle null, undefined, or non-array responses
    if (!users || (Array.isArray(users) && users.length === 0)) {
      return { success: true, users: [] };
    }
    return { success: true, users: Array.isArray(users) ? users : [] };
  } catch (error) {
    console.error('Error fetching agency users:', error);
    return { success: false, error: 'Network error fetching users' };
  }
};

/**
 * Get user details with permissions
 * @param {number} agencyId - The agency ID
 * @param {number} userId - The user ID
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export const getUserDetails = async (agencyId, userId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/users/${userId}`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch user details' };
    }

    const user = await parseResponse(response);
    return { success: true, user };
  } catch (error) {
    console.error('Error fetching user details:', error);
    return { success: false, error: 'Network error fetching user details' };
  }
};

/**
 * Create a new user (backend sends welcome email with password reset link)
 * @param {number} agencyId - The agency ID
 * @param {Object} userData - User data
 * @param {string} userData.username - Username (3-50 chars, alphanumeric/dots/hyphens/underscores)
 * @param {string} userData.email - User email
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} [userData.phoneNumber] - Phone number (optional)
 * @param {number[]} [userData.permissionIds] - Array of permission IDs (optional)
 * @returns {Promise<{success: boolean, portalUserId?: number, error?: string}>}
 */
export const createUser = async (agencyId, userData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to create user' };
    }

    const result = await parseResponse(response);
    return { success: true, portalUserId: result.portalUserId };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: 'Network error creating user' };
  }
};

/**
 * Update user and permissions
 * @param {number} agencyId - The agency ID
 * @param {number} userId - The user ID
 * @param {Object} userData - Updated user data
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.phoneNumber - Phone number
 * @param {boolean} userData.isActive - Active status
 * @param {number[]} userData.permissionIds - Array of permission IDs
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export const updateUser = async (agencyId, userId, userData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update user' };
    }

    const user = await parseResponse(response);
    return { success: true, user };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error: 'Network error updating user' };
  }
};

/**
 * Update agency profile (contact info and addresses)
 * @param {number|string} agencyId
 * @param {Object} profileData - { phone, fax, mobile, email, physicalAddress, mailingAddress }
 * @returns {Promise<{success: boolean, agency?: Object, error?: string}>}
 */
export const updateAgencyProfile = async (agencyId, profileData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/agencies/${agencyId}/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update agency profile' };
    }

    const agency = await parseResponse(response);
    return { success: true, agency };
  } catch (error) {
    console.error('Error updating agency profile:', error);
    return { success: false, error: 'Network error updating agency profile' };
  }
};

/**
 * Get agencies for user management (with user counts)
 * @returns {Promise<{success: boolean, agencies?: Array, error?: string}>}
 */
export const getAgenciesForManagement = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/agencies`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch agencies' };
    }

    const agencies = await parseResponse(response);
    return { success: true, agencies };
  } catch (error) {
    console.error('Error fetching agencies for management:', error);
    return { success: false, error: 'Network error fetching agencies' };
  }
};

/**
 * Get available permissions for an agency
 * @param {string|number} agencyId - Agency to fetch assignable permissions for
 * @returns {Promise<{success: boolean, permissions?: Array, error?: string}>}
 */
export const getAvailablePermissions = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/permissions/available`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch permissions' };
    }

    const permissions = await parseResponse(response);
    return { success: true, permissions };
  } catch (error) {
    console.error('Error fetching permissions:', error);
    return { success: false, error: 'Network error fetching permissions' };
  }
};

/**
 * Resend welcome email to a user who hasn't set their password yet
 * @param {number} agencyId - The agency ID
 * @param {number} userId - The user ID
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const resendWelcomeEmail = async (agencyId, userId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/users/${userId}/resend-welcome`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to resend welcome email' };
    }

    const result = await parseResponse(response);
    return { success: true, message: result.message };
  } catch (error) {
    console.error('Error resending welcome email:', error);
    return { success: false, error: 'Network error resending welcome email' };
  }
};

// ============================================
// Profile / User Info Endpoints
// ============================================

/**
 * Get current user info
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export const getUserInfo = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/info`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch user info' };
    }

    const user = await parseResponse(response);
    return { success: true, user };
  } catch (error) {
    console.error('Error fetching user info:', error);
    return { success: false, error: 'Network error fetching user info' };
  }
};

/**
 * Get user's agencies with capabilities
 * @returns {Promise<{success: boolean, agencies?: Array, error?: string}>}
 */
export const getUserAgencies = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/agencies`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch agencies' };
    }

    const agencies = await parseResponse(response);
    return { success: true, agencies };
  } catch (error) {
    console.error('Error fetching user agencies:', error);
    return { success: false, error: 'Network error fetching agencies' };
  }
};

/**
 * Update current user's own profile (self-service)
 * @param {Object} profileData - Profile data to update
 * @param {string} profileData.firstName - First name
 * @param {string} profileData.lastName - Last name
 * @param {string} [profileData.phoneNumber] - Phone number (optional)
 * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
 */
export const updateOwnProfile = async (profileData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/profile`, {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update profile' };
    }

    const result = await parseResponse(response);
    return { success: true, user: result.user };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error: 'Network error updating profile' };
  }
};

/**
 * Get user's permissions
 * @returns {Promise<{success: boolean, permissions?: Array, error?: string}>}
 */
export const getUserPermissions = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/permissions`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch permissions' };
    }

    const permissions = await parseResponse(response);
    return { success: true, permissions };
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return { success: false, error: 'Network error fetching permissions' };
  }
};

/**
 * Get agency preferences
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, preferences?: Array, error?: string}>}
 */
export const getAgencyPreferences = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/${agencyId}/preferences`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch preferences' };
    }

    const preferences = await parseResponse(response);
    return { success: true, preferences };
  } catch (error) {
    console.error('Error fetching agency preferences:', error);
    return { success: false, error: 'Network error fetching preferences' };
  }
};

/**
 * Update agency preference
 * @param {number} prefId - The preference ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateAgencyPreference = async (prefId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/preferences/${prefId}`, {
      method: 'PATCH',
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update preference' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating preference:', error);
    return { success: false, error: 'Network error updating preference' };
  }
};

/**
 * Get agency notification schedule
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, schedule?: Array, error?: string}>}
 */
export const getNotificationSchedule = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/${agencyId}/notification-schedule`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch notification schedule' };
    }

    const schedule = await parseResponse(response);
    return { success: true, schedule };
  } catch (error) {
    console.error('Error fetching notification schedule:', error);
    return { success: false, error: 'Network error fetching notification schedule' };
  }
};

/**
 * Update notification day active status
 * @param {number} notificationId - The notification setting ID
 * @param {number} day - Day of week (0-6)
 * @param {boolean} active - Active status
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateNotificationDay = async (notificationId, day, active) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/user/notification-schedule/${notificationId}/day/${day}/active/${active}`,
      { method: 'PATCH' }
    );

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update notification' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating notification day:', error);
    return { success: false, error: 'Network error updating notification' };
  }
};

/**
 * Get agency external contacts
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, contacts?: Array, error?: string}>}
 */
export const getAgencyContacts = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user/${agencyId}/contacts`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch contacts' };
    }

    const contacts = await parseResponse(response);
    return { success: true, contacts };
  } catch (error) {
    console.error('Error fetching agency contacts:', error);
    return { success: false, error: 'Network error fetching contacts' };
  }
};

/**
 * Save (create or update) an agency external contact
 * agentContactId of 0 = new contact, existing ID = update
 * @param {Object} contact - Contact payload
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const saveAgencyContact = async (contact) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/user/contact/${contact.agentContactId}`,
      {
        method: 'POST',
        body: JSON.stringify(contact),
      }
    );

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to save contact' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving agency contact:', error);
    return { success: false, error: 'Network error saving contact' };
  }
};

/**
 * Delete an agency external contact
 * @param {number} agentContactId - The contact ID to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteAgencyContact = async (agentContactId) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/user/contact/${agentContactId}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to delete contact' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting agency contact:', error);
    return { success: false, error: 'Network error deleting contact' };
  }
};

/**
 * Get available (unlinked) producers for an agency
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, producers?: Array, error?: string}>}
 */
export const getAvailableProducers = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/${agencyId}/available-producers`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch available producers' };
    }

    const producers = await parseResponse(response);
    return { success: true, producers: Array.isArray(producers) ? producers : [] };
  } catch (error) {
    console.error('Error fetching available producers:', error);
    return { success: false, error: 'Network error fetching available producers' };
  }
};

// ============================================
// Executing Agent Endpoints
// ============================================

/**
 * Get executing agents for an agency
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, agents?: Array, error?: string}>}
 */
export const getExecutingAgents = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/user-management/agencies/${agencyId}/executing-agents`);

    if (!response.ok) {
      if (response.status === 404 || response.status === 204) {
        return { success: true, agents: [] };
      }
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch executing agents' };
    }

    const agents = await parseResponse(response);
    return { success: true, agents: Array.isArray(agents) ? agents : [] };
  } catch (error) {
    console.error('Error fetching executing agents:', error);
    return { success: false, error: 'Network error fetching executing agents' };
  }
};

/**
 * Update an executing agent's profile
 * @param {number} agencyId - The agency ID
 * @param {number} executingAgentId - The executing agent ID
 * @param {Object} profileData - Profile fields (all nullable; omitted fields set to null)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateExecutingAgentProfile = async (agencyId, executingAgentId, profileData) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/user-management/agencies/${agencyId}/executing-agents/${executingAgentId}/profile`,
      {
        method: 'PUT',
        body: JSON.stringify(profileData),
      }
    );

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update executing agent profile' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating executing agent profile:', error);
    return { success: false, error: 'Network error updating executing agent profile' };
  }
};

/**
 * Update notification schedule start/end times for a specific day
 * @param {number} notificationId - The notification time ID
 * @param {string} start - Start time (HH:MM format)
 * @param {string} end - End time (HH:MM format)
 * @param {number} day - Day index (0-6)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateNotificationTime = async (notificationId, start, end, day) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/user/notification-schedule/${notificationId}/start/${start}/end/${end}/day/${day}`,
      { method: 'PATCH' }
    );

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update notification time' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating notification time:', error);
    return { success: false, error: 'Network error updating notification time' };
  }
};
